/**
 * Descarga del informe.
 *
 * El HTML y el CSV los arma `core/report/`, que es puro, así que lo que se
 * descarga es exactamente lo que se está viendo: no se recalcula nada, no se
 * vuelve a consultar WOX, y el archivo lleva la misma firma que la pantalla.
 *
 * El HTML trae su propio botón de imprimir, y desde ahí el navegador guarda en
 * PDF. Es la vía sensata: no hay un motor de PDF en el servidor que mantener, y
 * el PDF sale idéntico al informe, no de una segunda plantilla que se
 * desincroniza a los tres meses.
 *
 * ## El botón dice qué pasó (07/10/2026)
 *
 * En la revisión del Laboratorio se pulsó «Descargar informe» y no apareció
 * archivo, ruta ni error. El botón no decía nada nunca: un fallo al armar el
 * informe se iba a la consola, y la URL del archivo se revocaba en la misma
 * instrucción del clic, lo que en algunos navegadores cancela la descarga sin
 * aviso. Ahora la URL se libera un minuto después, y el botón dice qué se
 * generó o por qué no. Dice «se generó», no «se descargó»: si el archivo llega
 * a la carpeta lo decide el navegador, y desde aquí no se puede saber.
 *
 * ## Fallo al generar frente a resultado incierto (08/10/2026)
 *
 * Un error al armar el archivo o antes del clic permite decir «no se descargó
 * nada». Un error durante el clic no: el navegador pudo haber empezado la
 * descarga, y decir que no la hubo invita a repetirla. El enlace temporal y la
 * URL se limpian en los dos casos. La indicación de imprimir va solo con el
 * HTML: el CSV no se imprime a PDF.
 *
 * La salida es HTML. El PDF sale de Imprimir en el navegador; aquí no se genera
 * ni se descarga ningún PDF.
 */

import { useState } from "react";

import type { AnalysisResponse } from "@core/contracts/api.ts";
import type { DrivingAnalysisV5 } from "@core/pipeline/driving-analysis-v5.ts";
import { buildClientReportHtml } from "@core/report/client-report-html.ts";
import { buildEpisodesCsv } from "@core/report/episodes-csv.ts";

/** Cuánto se mantiene viva la URL del archivo después del clic. */
const REVOKE_AFTER_MS = 60_000;

/**
 * La entrega falló. `clicked` dice si el clic alcanzó a ejecutarse: antes del
 * clic, el navegador no recibió nada; durante el clic, no se sabe si la descarga
 * empezó.
 */
class DeliveryError extends Error {
  constructor(
    readonly clicked: boolean,
    readonly original: unknown,
  ) {
    super(original instanceof Error ? original.message : "");
  }
}

/**
 * Entrega el archivo al navegador y devuelve su tamaño en bytes.
 *
 * El enlace temporal se quita y la URL se libera pase lo que pase. Si el clic
 * llegó a ejecutarse, la URL se libera un minuto después, aunque el clic haya
 * lanzado un error: revocarla en el acto puede cancelar una descarga que sí
 * empezó. Si el clic no llegó a ejecutarse, se libera en el acto.
 */
function download(filename: string, contents: string, mime: string): number {
  let url: string;
  let blob: Blob;
  try {
    blob = new Blob([contents], { type: `${mime};charset=utf-8` });
    url = URL.createObjectURL(blob);
  } catch (error) {
    throw new DeliveryError(false, error);
  }
  const anchor = document.createElement("a");
  let clicked = false;
  try {
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    clicked = true;
    anchor.click();
  } catch (error) {
    throw new DeliveryError(clicked, error);
  } finally {
    anchor.remove();
    if (clicked) setTimeout(() => URL.revokeObjectURL(url), REVOKE_AFTER_MS);
    else URL.revokeObjectURL(url);
  }
  return blob.size;
}

/**
 * Lo que el botón puede afirmar:
 *  - `ok`: se generó y se entregó al navegador. Si llegó a la carpeta lo decide
 *    el navegador.
 *  - `error`: falló antes del clic. El navegador no recibió nada.
 *  - `incierto`: el clic lanzó un error. No se sabe si la descarga empezó, así
 *    que no se dice que no se descargó.
 */
type Outcome =
  | { tone: "ok"; text: string; isHtml: boolean }
  | { tone: "error"; text: string }
  | { tone: "incierto"; text: string };

const detailOf = (error: unknown): string => (error instanceof Error && error.message ? ` (${error.message})` : "");

/** Arma y entrega un archivo, y dice qué pasó. Nunca falla en silencio. */
function attempt(what: string, build: () => { filename: string; contents: string; mime: string }): Outcome {
  let built: { filename: string; contents: string; mime: string };
  try {
    built = build();
  } catch (error) {
    return { tone: "error", text: `No se pudo generar ${what}${detailOf(error)}. No se descargó nada.` };
  }
  const { filename, contents, mime } = built;
  try {
    const bytes = download(filename, contents, mime);
    const kb = (bytes / 1024).toLocaleString("es-CO", { maximumFractionDigits: 1 });
    return {
      tone: "ok",
      isHtml: mime === "text/html",
      text:
        `Se generó ${filename} (${kb} KB) y se entregó al navegador. Si no aparece en tu carpeta de ` +
        `descargas, revisa si el navegador bloqueó la descarga.`,
    };
  } catch (error) {
    const detail = detailOf(error instanceof DeliveryError ? error.original : error);
    if (error instanceof DeliveryError && error.clicked) {
      return {
        tone: "incierto",
        text:
          `Se generó ${filename}, pero el navegador respondió con un error al entregarlo${detail}. ` +
          `No se sabe si la descarga empezó: revisa tu carpeta de descargas antes de volver a intentarlo.`,
      };
    }
    return {
      tone: "error",
      text: `Se generó ${filename}, pero no se pudo entregar al navegador${detail}. No se descargó nada.`,
    };
  }
}

/** `GuardIA_TST103_20260901_20260903` — ordena solo por nombre en el explorador. */
function baseName(result: AnalysisResponse): string {
  const analysis = result.analysis as unknown as DrivingAnalysisV5;
  const compact = (iso: string) => iso.slice(0, 10).replace(/-/gu, "");
  const plate = (result.vehicle.plate ?? result.vehicle.deviceId).replace(/[^\w-]/gu, "");
  return `GuardIA_${plate}_${compact(analysis.period.requestedFrom)}_${compact(analysis.period.requestedTo)}`;
}

export function DownloadReport({ result }: { result: AnalysisResponse }) {
  const analysis = result.analysis as unknown as DrivingAnalysisV5;
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
        onClick={() =>
          setOutcome(
            attempt("el informe", () => ({
              filename: `${baseName(result)}.html`,
              contents: buildClientReportHtml({
                analysis,
                sampling: result.sampling,
                provenance: result.provenance,
                vehicle: result.vehicle,
                // Se pasa tal como llegó, sin recalcular: el informe descargable
                // tiene que declarar lo mismo que la pantalla sobre el mismo
                // período. Cuando falta, el generador cae al criterio anterior.
                measurability: result.measurability,
                // El instante de emisión se inyecta aquí, en el borde: el
                // generador es puro y no tiene reloj.
                generatedAtIso: new Date().toISOString(),
              }),
              mime: "text/html",
            })),
          )
        }
      >
        Descargar informe
      </button>

      <button
        type="button"
        className="rounded-md border border-zinc-300 px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
        disabled={analysis.technicalDriving.episodes.length === 0}
        onClick={() =>
          setOutcome(
            attempt("el CSV de episodios", () => ({
              filename: `${baseName(result)}_episodios.csv`,
              contents: buildEpisodesCsv(analysis, {
                plate: result.vehicle.plate,
                deviceId: result.vehicle.deviceId,
              }),
              mime: "text/csv",
            })),
          )
        }
      >
        Episodios en CSV
      </button>

      {outcome?.tone === "ok" ? (
        <p role="status" className="w-full text-xs text-zinc-600 dark:text-zinc-400">
          {outcome.text}
          {outcome.isHtml ? " El archivo es HTML, no PDF: para tener un PDF, ábrelo y usa Imprimir → Guardar como PDF." : null}
        </p>
      ) : null}
      {outcome?.tone === "error" || outcome?.tone === "incierto" ? (
        <p role="alert" className="w-full text-xs text-amber-700 dark:text-amber-500">
          {outcome.text}
        </p>
      ) : null}
    </div>
  );
}
